"use client";

import { useRef, useState } from "react";
import type { FaqItem } from "@/lib/faqData";
import { useTrackEvent } from "@/hooks/useTrackEvent";

type FaqAccordionProps = {
  items: FaqItem[];
  idPrefix: string;
  defaultOpenId?: string;
  className?: string;
  eventName?: string;
  eventProperties?: Record<string, unknown>;
};

function getInitialOpenItems(items: FaqItem[], defaultOpenId?: string) {
  if (!items.length) return {};
  const targetId = defaultOpenId ?? items[0].id;
  return { [targetId]: true };
}

export function FaqAccordion({
  items,
  idPrefix,
  defaultOpenId,
  className,
  eventName,
  eventProperties,
}: FaqAccordionProps) {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>(() =>
    getInitialOpenItems(items, defaultOpenId),
  );
  const hasInteracted = useRef(false);
  const trackEvent = useTrackEvent();

  const toggleItem = (id: string) => {
    hasInteracted.current = true;
    setOpenItems((prev) => {
      const nextOpen = !prev[id];
      if (nextOpen && eventName) {
        const item = items.find((entry) => entry.id === id);
        trackEvent(eventName, {
          faq_id: id,
          faq_question: item?.question,
          ...eventProperties,
        });
      }
      return { ...prev, [id]: nextOpen };
    });
  };

  return (
    <div className={`space-y-3 ${className ?? ""}`.trim()}>
      {items.map((item) => {
        const panelId = `${idPrefix}-${item.id}`;
        const buttonId = `${panelId}-button`;
        const isOpen = Boolean(openItems[item.id]);

        return (
          <article
            key={item.id}
            className="rounded-2xl border border-border-subtle bg-surface/70 p-4 shadow-soft"
          >
            <button
              type="button"
              id={buttonId}
              className="flex w-full items-center justify-between gap-4 text-left text-sm font-semibold text-foreground dark:text-offwhite"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggleItem(item.id)}
            >
              <span>{item.question}</span>
              <span className="text-xs uppercase tracking-[0.2em] text-mist">{isOpen ? "–" : "+"}</span>
            </button>
            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              className={`grid transition-[grid-template-rows,opacity] duration-200 ${
                isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <p className="pt-3 text-sm leading-relaxed text-text-secondary">{item.answer}</p>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
